import { Autocomplete, AutocompleteMulti, getRenderTagsForAutocompleteMulti } from '@uplift/ui-v2';
import { FieldArrayRenderProps, Form, useFormikContext } from 'formik';
import { useSectionStyles } from '../styles';
import clsx from 'clsx';
import { ClinicalNotesFormType, ClinicalNotesTreatmentType } from '../../types';
import { useClinicalNotesContext } from '../../clinical-notes.context';
import { useMemo } from 'react';
import { useL10n } from '@uplift/hooks';
import { useTreatmentApproachInterventionsQuery } from '@uplift/queries';

export type TreatmentFormProps = {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
};

export const TreatmentForm: React.VFC<TreatmentFormProps> = ({ arrayHelpers, index }) => {
  const formik = useFormikContext<ClinicalNotesFormType>();
  const { treatmentApproachId } = formik.values.treatment[index];
  const { data: interventions, isInitialLoading: isLoading } = useTreatmentApproachInterventionsQuery(treatmentApproachId, {
    enabled: !!treatmentApproachId
  });
  const l10n = useL10n();
  const { state: { treatments } } = useClinicalNotesContext();
  const { classes } = useSectionStyles();
  const createName = (name: keyof ClinicalNotesTreatmentType) => `${arrayHelpers.name}[${index}].${name}`;
  const approachName = createName('treatmentApproachId');
  const interventionsName = createName('interventionIds');

  const setFieldValue = formik.setFieldValue;
  const handleSetValue = (value: string) => {
    if (treatmentApproachId !== value) {
      setFieldValue(interventionsName, [], true);
    }
  };

  const interventionOptions = treatmentApproachId && !isLoading && interventions ? interventions : [];

  // optimization
  const selectedTreatmentIdHash = useMemo(() => {
    return formik.values.treatment.reduce((r, v) => ({ ...r, [v.treatmentApproachId]: true }), {} as {[key: string]: boolean});
  }, [ formik.values.treatment ]);

  const noOptionsText = useMemo(() => {
    if (isLoading) return '';
    if (treatmentApproachId) return l10n.clinicalNotes.noInterventionsForThisTreatment;
    return l10n.clinicalNotes.selectApproachToSeeInterventionOptions;
  }, [ isLoading, l10n.clinicalNotes.noInterventionsForThisTreatment, l10n.clinicalNotes.selectApproachToSeeInterventionOptions, treatmentApproachId ]);

  return (
    <Form className={clsx([ classes.formGroup, classes.gap2 ])}>
      <Autocomplete
        color="secondary"
        label={l10n.clinicalNotes.approach}
        name={approachName}
        classes={{
          inputRoot: classes.inputBackgroundWhite
        }}
        placeholder={l10n.clinicalNotes.selectApproach}
        getOptionLabel={(opt) => opt?.name}
        getOptionDisabled={(opt) => opt?.id in selectedTreatmentIdHash}
        transformValue={(opt) => opt?.id}
        options={treatments}
        setValue={handleSetValue}
      />
      <AutocompleteMulti
        extractId
        color="secondary"
        label={l10n.clinicalNotes.interventions}
        name={interventionsName}
        classes={{
          inputRoot: classes.inputBackgroundWhite
        }}
        placeholder={l10n.clinicalNotes.selectInterventions}
        getOptionLabel={(opt) => opt?.name}
        options={interventionOptions ?? []}
        noOptionsText={noOptionsText}
        renderTags={getRenderTagsForAutocompleteMulti((opt) => opt?.name)}
      />
    </Form>
  );
};
